import React, { useState, useEffect, useRef } from 'react';
import { AutoComplete, Input, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { AddressServiceFactory, AddressSuggestion, YandexAddressService } from '../../services/addressService';
import { useLazyGetFrequentAddressesQuery, useAddFrequentAddressMutation } from '../../store/api/frequentAddressApi';

interface AddressAutocompleteProps {
  value?: string;
  onChange?: (value: string, suggestion?: AddressSuggestion) => void;
  placeholder?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'Введите адрес',
  disabled = false,
  style
}) => {
  const [options, setOptions] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState(value || '');
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const addressService = useRef(AddressServiceFactory.createDefaultService());
  const yandexFallbackService = useRef<YandexAddressService | null>(
    process.env.REACT_APP_YANDEX_API_KEY ? new YandexAddressService(process.env.REACT_APP_YANDEX_API_KEY) : null
  );
  const LOCAL_HISTORY_KEY = 'logistics_address_history';
  const LOCAL_HISTORY_SYNC_FLAG = 'logistics_address_history_synced_v1';
  const [getFrequentAddresses, { isLoading: frequentAddressesLoading }] = useLazyGetFrequentAddressesQuery();
  const [addFrequentAddress] = useAddFrequentAddressMutation();

  // Один раз мигрируем старые частые адреса из localStorage (которые сохранялись только на фронте)
  useEffect(() => {
    const syncLocalHistoryToBackend = async () => {
      try {
        // Уже синхронизировано — выходим
        if (localStorage.getItem(LOCAL_HISTORY_SYNC_FLAG)) return;
        const raw = localStorage.getItem(LOCAL_HISTORY_KEY);
        if (!raw) return;
        const history: Array<{ address: string; count?: number }> = JSON.parse(raw);
        if (!Array.isArray(history) || history.length === 0) return;

        // Берём топ-20 адресов и отправляем на бэкенд
        const uniqueAddresses = Array.from(
          new Map(history.map((h) => [h.address?.trim(), h.address?.trim()])).values()
        )
          .filter((addr) => addr && addr.length >= 3)
          .slice(0, 20) as string[];

        if (uniqueAddresses.length === 0) return;

        await Promise.allSettled(uniqueAddresses.map((addr) => addFrequentAddress({ address: addr }).unwrap()));

        // Помечаем, что синхронизация сделана
        localStorage.setItem(LOCAL_HISTORY_SYNC_FLAG, '1');
      } catch (e) {
        // Не критично; в худшем случае пользователь увидит меньше подсказок
        console.warn('Не удалось синхронизировать локальную историю адресов:', e);
      }
    };

    syncLocalHistoryToBackend();
  }, [addFrequentAddress]);

  // Загружаем частые адреса при инициализации
  useEffect(() => {
    if (!searchValue) {
      loadFrequentAddresses();
    }
  }, []);

  // Функция для загрузки часто используемых адресов из API
  const loadFrequentAddresses = async (query: string = '') => {
    try {
      const result = await getFrequentAddresses({ query, limit: query ? 10 : 5 }).unwrap();
      const suggestions: AddressSuggestion[] = result.map(item => ({
        value: item.address,
        label: (
          <div>
            <div>{item.address}</div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              Использовано {item.useCount} раз
            </div>
          </div>
        ),
        isHistory: true
      }));
      setOptions(suggestions);
    } catch (error) {
      console.error('Error loading frequent addresses:', error);
      setOptions([]);
    }
  };

  // Добавляем отладочную информацию
  useEffect(() => {
    const yandexKey = process.env.REACT_APP_YANDEX_API_KEY;
    console.debug('🔑 API ключ Яндекс.Карты:', yandexKey ? 'Найден' : 'НЕ НАЙДЕН');
  }, []);

  // Функция поиска адресов через сервис
  const searchAddresses = async (query: string): Promise<AddressSuggestion[]> => {
    if (!query || query.length < 3) return [];

    console.log('🔍 Поиск адресов:', query);
    
    try {
      const suggestions = await addressService.current.searchAddresses(query);
      console.log('✅ Найдено адресов:', suggestions.length);
      return suggestions;
    } catch (error) {
      console.error('❌ Ошибка поиска адресов:', error);
      return [];
    }
  };

  // Обработчик изменения значения поиска
  const handleSearch = async (searchText: string) => {
    setSearchValue(searchText);
    
    // Очищаем предыдущий таймаут
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Показываем частые адреса при коротком запросе
    if (searchText.length < 3) {
      await loadFrequentAddresses(searchText);
      setLoading(false);
      return;
    }

    // Устанавливаем новый таймаут для дебаунсинга
    searchTimeoutRef.current = setTimeout(async () => {
      if (searchText.length >= 3) {
        setLoading(true);
        try {
          // Параллельно загружаем частые адреса и ищем через Яндекс.Карты
          const [frequentResult, yandexSuggestions] = await Promise.allSettled([
            getFrequentAddresses({ query: searchText, limit: 10 }).unwrap(),
            searchAddresses(searchText)
          ]);

          // Обрабатываем частые адреса
          const frequentSuggestions: AddressSuggestion[] = 
            frequentResult.status === 'fulfilled'
              ? frequentResult.value.map(item => ({
                  value: item.address,
                  label: (
                    <div>
                      <div>{item.address}</div>
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        Использовано {item.useCount} раз
                      </div>
                    </div>
                  ),
                  isHistory: true
                }))
              : [];

          // Обрабатываем результаты из Яндекс.Карт (через backend)
          const yandexOptions: AddressSuggestion[] = 
            yandexSuggestions.status === 'fulfilled'
              ? yandexSuggestions.value
              : [];

          // Объединяем результаты: сначала частые адреса, потом из Яндекс.Карт
          let combinedOptions = [...frequentSuggestions, ...yandexOptions];

          // Если из backend ничего не пришло, пробуем прямой вызов Яндекс с фронтенд ключом (fallback)
          if (combinedOptions.length === 0 && yandexFallbackService.current) {
            try {
              const direct = await yandexFallbackService.current.searchAddresses(searchText);
              combinedOptions = [...frequentSuggestions, ...direct];
            } catch (e) {
              // игнорируем, fallback не критичен
            }
          }

          // Убираем дубликаты по value, чтобы не было предупреждений о ключах
          const uniqueOptions = Array.from(
            new Map(combinedOptions.filter(o => o?.value).map((o) => [o.value, o]))
            .values()
          );

          setOptions(uniqueOptions);
        } catch (error) {
          console.error('Ошибка при поиске адресов:', error);
          // В случае ошибки все равно пытаемся показать частые адреса
          try {
            await loadFrequentAddresses(searchText);
          } catch (e) {
            setOptions([]);
          }
        } finally {
          setLoading(false);
        }
      } else {
        await loadFrequentAddresses(searchText);
        setLoading(false);
      }
    }, 300); // Задержка 300мс
  };

  // Обработчик выбора адреса
  const handleSelect = async (selectedValue: string, option: any) => {
    setSearchValue(selectedValue);
    if (process.env.NODE_ENV !== 'production') {
      console.log('[AddressAutocomplete] selected address', selectedValue, option);
    }
    // Сохраняем выбранный адрес в базу данных через API
    try {
      await addFrequentAddress({ address: selectedValue }).unwrap();
    } catch (error) {
      console.error('Error saving frequent address:', error);
    }
    
    // Если option не содержит координаты (например, частый адрес из БД), 
    // пытаемся найти его в options и передать suggestion с координатами
    let suggestion: AddressSuggestion | undefined = option;
    if (!option?.coordinates) {
      // Ищем suggestion в текущих options
      const foundOption = options.find(opt => opt.value === selectedValue);
      if (foundOption && foundOption.coordinates) {
        suggestion = foundOption;
      }
    }
    
    onChange?.(selectedValue, suggestion);
  };

  // Обработчик изменения значения
  const handleChange = (inputValue: string) => {
    setSearchValue(inputValue);
    if (process.env.NODE_ENV !== 'production') {
      console.log('[AddressAutocomplete] input change', inputValue);
    }
    onChange?.(inputValue);
  };

  // Очистка таймаута при размонтировании
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Обновляем значение при изменении пропса
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && value !== searchValue) {
      console.log('[AddressAutocomplete] hydrate from value prop', value);
    }
    setSearchValue(value || '');
  }, [value]);

  return (
    <AutoComplete
      value={searchValue}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelect}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      style={style}
      notFoundContent={(loading || frequentAddressesLoading) ? <Spin size="small" /> : null}
    >
      <Input
        prefix={<SearchOutlined />}
        allowClear
        size="large"
      />
    </AutoComplete>
  );
};

export default AddressAutocomplete; 